import { useState, useEffect, useMemo, useRef } from 'react';
import { collection, onSnapshot, query, where, doc, writeBatch, getDoc } from 'firebase/firestore';
import { appId, initialCities, PROJECT_STATUSES } from '../constants/appConstants';
import { generatePendencyId } from '../utils/pendencyUtils';

/**
 * Hook customizado para buscar e gerenciar os dados principais da aplicação do Firestore.
 * @param {import('firebase/firestore').Firestore} db - A instância do Firestore.
 * @param {object} user - O objeto de usuário autenticado.
 * @param {object} userData - Os dados do perfil do usuário do Firestore.
 * @returns {{
 * loading: boolean,
 * projects: Array,
 * pendencies: Array,
 * usersList: Array,
 * userNotifications: Array,
 * irregularityNotifications: Array,
 * citiesData: Array,
 * setCitiesData: Function,
 * settings: object,
 * connectionError: string | null
 * }} Objeto contendo os dados da aplicação e o estado de carregamento.
 */
export const useAppData = (db, user, userData) => {
    const [loading, setLoading] = useState(true);
    const [connectionError, setConnectionError] = useState(null);
    
    // Estados para os dados principais
    const [projects, setProjects] = useState([]);
    const [pendencies, setPendencies] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [userNotifications, setUserNotifications] = useState([]);
    const [irregularityNotifications, setIrregularityNotifications] = useState([]);
    const [citiesData, setCitiesData] = useState([]);
    const [settings, setSettings] = useState({ polePrice: 0, darkMapEnabled: false });
    const [biStats, setBiStats] = useState(null);

    useEffect(() => {
        if (!db || !user || !userData || userData.role === 'pending') {
            setLoading(false);
            return;
        }

        setLoading(true);
        const unsubscribers = [];

        try {
            const pendenciesRef = collection(db, `artifacts/${appId}/public/data/pendencies`);
            unsubscribers.push(onSnapshot(pendenciesRef, (snapshot) => {
                setPendencies(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            }));
            
            unsubscribers.push(onSnapshot(collection(db, 'users'), (snapshot) => {
                setUsersList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            }));
            
            const qNotif = query(collection(db, 'notifications'), where('userId', '==', user.uid), where('read', '==', false));
            unsubscribers.push(onSnapshot(qNotif, (snapshot) => {
                // ⚡ Bolt: Pre-calculate timestamps during map to avoid O(N log N) new Date() creations in sort
                const sortedNotifs = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return { id: doc.id, ...data, _createdAtMs: new Date(data.createdAt).getTime() };
                    })
                    .sort((a, b) => b._createdAtMs - a._createdAtMs);
                setUserNotifications(sortedNotifs);
            }));

            const irregularityNotificationsRef = collection(db, `artifacts/${appId}/public/data/irregularity_notifications`);
            unsubscribers.push(onSnapshot(irregularityNotificationsRef, (snapshot) => {
                setIrregularityNotifications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            }));
            
            const citiesPath = `artifacts/${appId}/public/data/cities`;
            unsubscribers.push(onSnapshot(query(collection(db, citiesPath)), async (snapshot) => {
                if (snapshot.empty) {
                    const batch = writeBatch(db);
                    initialCities.forEach(city => batch.set(doc(db, citiesPath, city.id), city));
                    await batch.commit();
                } else {
                    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setCitiesData(initialCities.map(ic => data.find(d => d.id === ic.id) || ic));
                }
            }));

            const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'config');
            unsubscribers.push(onSnapshot(settingsRef, (docSnap) => {
                const defaultSettings = { polePrice: 0, darkMapEnabled: false, pinnedPendencies: [] };
                setSettings(docSnap.exists() ? { ...defaultSettings, ...docSnap.data() } : defaultSettings);
            }));

            const biStatsRef = doc(db, `artifacts/${appId}/public/bi_stats`);
            unsubscribers.push(onSnapshot(biStatsRef, (docSnap) => {
                if (docSnap.exists()) {
                    setBiStats(docSnap.data());
                }
            }, (error) => {
                console.error("Error fetching BI stats:", error);
            }));

            if (userData.role === 'administrador' || userData.role === 'tecnico') {
                const projectsRef = collection(db, `artifacts/${appId}/public/data/projects`);
                unsubscribers.push(onSnapshot(projectsRef, (snapshot) => {
                    setProjects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                }));
            }

            setLoading(false);
        } catch (e) {
            console.error("Erro ao buscar dados do Firestore:", e);
            setConnectionError("Falha ao conectar com o banco de dados.");
            setLoading(false);
        }

        return () => unsubscribers.forEach(unsub => unsub());
    }, [db, user, userData]);

return { loading, projects, pendencies, usersList, userNotifications, irregularityNotifications, citiesData, setCitiesData, settings, connectionError, biStats };
};

/**
 * Hook para realizar cálculos e memoizações com base nos dados da aplicação.
 * @param {object} data - Objeto contendo os dados brutos da aplicação.
 * @returns {object} Objeto contendo os dados calculados e filtrados.
 */
// Helper to normalize strings for robust comparison
const normalizeString = (str) => {
    return str ? str.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim() : "";
};

const PROTOCOL_SPLIT_REGEX = /[/,;]/;

export const useAppCalculations = ({ projects, pendencies, usersList, citiesData, settings, showOnlyPending, includeNonRejected = false }) => {
    
    // Persistent caches to avoid expensive string operations on every render
    const normCacheRef = useRef(new Map());
    const protocolCacheRef = useRef(new Map());

    const calculatedCitiesData = useMemo(() => {
        const regularizedCounts = {};
        const normCache = normCacheRef.current;

        // Helper para memoizar a normalização de strings
        const getNormalized = (str) => {
            if (!str) return "";
            if (normCache.has(str)) return normCache.get(str);
            const norm = normalizeString(str);
            normCache.set(str, norm);
            return norm;
        };

        projects.forEach(project => {
            const statusNorm = getNormalized(project.status);
            // Verifica tanto o valor ('aprovado') quanto o rótulo ('vistoria aprovada') para garantir contagem correta
            const isApproved = statusNorm === 'aprovado' || statusNorm === 'vistoria aprovada';

            // Se incluir não-reprovados: conta tudo que não for 'reprovado' E não for 'cancelado'
            const isRegularized = includeNonRejected
                ? (statusNorm !== 'reprovado' && statusNorm !== 'cancelado')
                : isApproved;

            if (isRegularized && project.cidade) {
                const cityKey = getNormalized(project.cidade);
                // Prioriza o campo 'totalPostes', usando o array como fallback
                const poleCount = (project.totalPostes !== undefined && project.totalPostes !== null)
                    ? Number(project.totalPostes)
                    : (Array.isArray(project.postes) ? project.postes.length : 0);

                regularizedCounts[cityKey] = (regularizedCounts[cityKey] || 0) + poleCount;
            } else if (isRegularized && !project.cidade) {
                 const poleCount = (project.totalPostes !== undefined && project.totalPostes !== null)
                    ? Number(project.totalPostes)
                    : (Array.isArray(project.postes) ? project.postes.length : 0);
                 console.warn(`[Dashboard] Projeto ${project.protocolo || project.id} regularizado mas sem cidade definida. Postes ignorados: ${poleCount}`);
            }
        });

        return citiesData.map(city => {
            const normNome = getNormalized(city.nome);
            const normId = getNormalized(city.id);

            let count = regularizedCounts[normNome] || 0;

            // If the ID is different from the name (normalized), check if we have counts stored under the ID as well
            if (normId !== normNome) {
                count += (regularizedCounts[normId] || 0);
            }

            return {
                ...city,
                regularizados: city.licenciados !== undefined ? city.licenciados : count
            };
        });
    }, [citiesData, projects, includeNonRejected]);

    const duplicateStats = useMemo(() => {
        // Optimized: Use nested Map<lat, Map<lon, Set<protocols>>> to avoid string key allocation.
        const poleCoordinates = new Map();
        // Optimized: Use persistent cache for processed protocols.
        const protocolCache = protocolCacheRef.current;

        // Optimized: Avoid flatMap to prevent large array allocation.
        // Iterate projects and poles directly.
        for (const project of projects) {
            const poles = project.postes;
            if (!poles || !Array.isArray(poles)) continue;

            for (const pole of poles) {
                // Optimization: Skip poles without protocol immediately.
                if (!pole.protocoloLicenciamento) continue;

                if (pole.latitude && pole.longitude) {
                    // Optimization: Use Math.round instead of toFixed(6) for ~100x faster key generation
                    const latInt = Math.round(pole.latitude * 1000000);
                    const lonInt = Math.round(pole.longitude * 1000000);

                    let lonMap = poleCoordinates.get(latInt);
                    if (!lonMap) {
                        lonMap = new Map();
                        poleCoordinates.set(latInt, lonMap);
                    }

                    let protocolsSet = lonMap.get(lonInt);
                    if (!protocolsSet) {
                        protocolsSet = new Set();
                        lonMap.set(lonInt, protocolsSet);
                    }

                    const rawProtocol = pole.protocoloLicenciamento;
                    let processedProtocols = protocolCache.get(rawProtocol);

                    if (!processedProtocols) {
                        processedProtocols = [];
                        const lower = rawProtocol.toLowerCase();
                        if (PROTOCOL_SPLIT_REGEX.test(lower)) {
                            const protocols = lower.split(PROTOCOL_SPLIT_REGEX);
                            for (let i = 0; i < protocols.length; i++) {
                                const p = protocols[i].trim();
                                if (p) processedProtocols.push(p);
                            }
                        } else {
                            const p = lower.trim();
                            if (p) processedProtocols.push(p);
                        }
                        protocolCache.set(rawProtocol, processedProtocols);
                    }

                    for (let i = 0; i < processedProtocols.length; i++) {
                        protocolsSet.add(processedProtocols[i]);
                    }
                }
            }
        }

        let extraProtocolCount = 0;
        const duplicatePolesList = [];

        // Iterate through the nested map structure
        for (const [latInt, lonMap] of poleCoordinates) {
            for (const [lonInt, protocolsSet] of lonMap) {
                if (protocolsSet.size > 1) {
                    const lat = latInt / 1000000;
                    const lon = lonInt / 1000000;
                    duplicatePolesList.push({ lat, lon, protocols: Array.from(protocolsSet) });
                    extraProtocolCount += (protocolsSet.size - 1);
                }
            }
        }

        return { extraProtocolCount, duplicatePolesList };
    }, [projects]);

    const appStats = useMemo(() => {
        const polePrice = settings.polePrice || 0;
        const totalRegularizedPoles = calculatedCitiesData.reduce((acc, city) => acc + (city.regularizados || 0), 0);
        const totalCurrentCost = totalRegularizedPoles * polePrice;
        const totalPoles = calculatedCitiesData.reduce((acc, city) => acc + (city.total || 0), 0);
        const totalProjectedCost = totalPoles * polePrice;

        const { extraProtocolCount, duplicatePolesList } = duplicateStats;
        const duplicatePoleCount = duplicatePolesList.length;
        const duplicateCost = extraProtocolCount * polePrice;

        return { totalCurrentCost, totalProjectedCost, duplicatePoleCount, duplicateCost, duplicatePolesList };
    }, [calculatedCitiesData, settings.polePrice, duplicateStats]);

    const chartData = useMemo(() => calculatedCitiesData.map(city => {
        // Nova metodologia: Prioriza dados do backend (licenciados) se existirem
        const licenciados = city.licenciados !== undefined ? city.licenciados : city.regularizados;
        const total = city.total || 0;
        // const reprovados = city.reprovados || 0; // Unused
        const desconhecidos = city.desconhecidos || 0;

        // Postes a licenciar = Desconhecidos (sem protocolo)
        const aRegularizar = desconhecidos;

        return {
            nome: city.nome,
            regularizados: licenciados,
            aRegularizar: aRegularizar,
            total: total,
            custoAtual: licenciados * (settings.polePrice || 0),
            custoProjetado: total * (settings.polePrice || 0),
        };
    }), [calculatedCitiesData, settings.polePrice]);

    const projectStatusChartData = useMemo(() => {
        // ... (código existente)
        const totalProjects = projects.length;
        if (totalProjects === 0) {
            return PROJECT_STATUSES.map(statusInfo => ({ name: statusInfo.label, value: 0, color: statusInfo.color, percentage: 0 }));
        }
        const projectCounts = projects.reduce((acc, project) => {
            const statusKey = project.status ? project.status.toLowerCase() : 'desconhecido';
            acc[statusKey] = (acc[statusKey] || 0) + 1;
            return acc;
        }, {});
        return PROJECT_STATUSES.map(statusInfo => {
            const statusKey = statusInfo.value.toLowerCase();
            const value = projectCounts[statusKey] || 0;
            return { name: statusInfo.label, value, color: statusInfo.color, percentage: (value / totalProjects) * 100 };
        });
    }, [projects]);

    // --- NOVO CÁLCULO PARA O GRÁFICO DE STATUS DE PENDÊNCIAS ---
    const pendencyStatusChartData = useMemo(() => {
        const totalPendencies = pendencies.length;
        const statusConfig = [
            { name: 'Pendente', color: '#f97316' }, // Laranja
            { name: 'Executado', color: '#0ea5e9' }  // Azul Céu
        ];

        if (totalPendencies === 0) {
            return statusConfig.map(s => ({ ...s, value: 0, percentage: 0 }));
        }

        const pendencyCounts = pendencies.reduce((acc, pendency) => {
            const status = pendency.status || 'Pendente';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        return statusConfig.map(s => {
            const value = pendencyCounts[s.name] || 0;
            return {
                ...s,
                value,
                percentage: (value / totalPendencies) * 100
            };
        });
    }, [pendencies]);

    const usersMap = useMemo(() => {
        // ... (código existente)
        const map = new Map();
        usersList.forEach(user => map.set(user.id, user));
        return map;
    }, [usersList]);

    const enrichedPendencies = useMemo(() => {
        // ... (código existente)
        // Optimized: Pre-compute values for sorting and display to avoid repeated calculations in child components.
        // Higher value = Higher Priority
        const priorityValues = { 'Alta': 3, 'Média': 2, 'Baixa': 1 };

        return pendencies
            .filter(p => {
                return !showOnlyPending || p.status === 'Pendente';
            })
            .map(p => {
                let dateTimestamp = 32503680000000; // 2999-12-31 fallback for missing dates (puts them at top if DESC)
                if (p.dataCadastro) {
                    if (typeof p.dataCadastro.toDate === 'function') {
                        dateTimestamp = p.dataCadastro.toDate().getTime();
                    } else if (typeof p.dataCadastro === 'number') {
                        dateTimestamp = p.dataCadastro;
                    } else {
                        const d = new Date(p.dataCadastro);
                        if (!isNaN(d.getTime())) {
                            dateTimestamp = d.getTime();
                        }
                    }
                }

                const displayId = generatePendencyId(p.id);
                const creatorUser = p.cadastradoPor?.uid ? usersMap.get(p.cadastradoPor.uid) : null;
                const assignedUser = p.executadoPor?.uid ? usersMap.get(p.executadoPor.uid) : null;

                const searchString = `
                    ${normalizeString(displayId)} |||
                    ${normalizeString(p.cidade)} |||
                    ${normalizeString(p.descricao)} |||
                    ${normalizeString(creatorUser?.displayName)} |||
                    ${normalizeString(assignedUser?.displayName)}
                `.toLowerCase();

                return {
                    ...p,
                    priorityValue: priorityValues[p.prioridade] || 0,
                    dateTimestamp,
                    displayId,
                    searchString
                };
            })
            .sort((a, b) => {
                // Priority: Alta(3) > Baixa(1). Descending.
                if (a.priorityValue !== b.priorityValue) {
                    return b.priorityValue - a.priorityValue;
                }
                // Date: Newest first (Descending timestamp)
                return b.dateTimestamp - a.dateTimestamp;
            });
    }, [pendencies, showOnlyPending]);

    const enrichedProjects = useMemo(() => {
        return projects.map(project => {
            // A Cloud Function agora popula `project.viewers` com os UIDs.
            if (!project.viewers || !Array.isArray(project.viewers)) {
                return { ...project, viewers: [] };
            }

            const viewersDetails = project.viewers
                .map(viewerUid => usersMap.get(viewerUid))
                .filter(Boolean); // Filtra caso um usuário tenha sido deletado mas seu UID permaneça.

            return { ...project, viewers: viewersDetails };
        });
    }, [projects, usersMap]);

    return { appStats, chartData, projectStatusChartData, usersMap, enrichedPendencies, pendencyStatusChartData, enrichedProjects, calculatedCitiesData };
};
